import React, { useState } from 'react';
import { Box, Select, MenuItem, CircularProgress, Tooltip, Typography, TextField } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

/**
 * Recurrence editor for a recurring contract obligation.
 *
 * Replaces the date picker that used to sit here. A single date was the wrong
 * control: the calendar treated it as the anchor day-of-month for every future
 * occurrence, so "15 Aug" silently meant "the 15th, forever" — and there was no
 * way to say "the last day", which is what most month-end obligations actually
 * are. Frequency was not editable at all.
 *
 * Anchors only apply to cadences that step in whole months. Weekly/daily/ad-hoc
 * have no day-of-month, so the anchor select is hidden rather than shown doing
 * nothing.
 *
 * Stored server-side in Postgres, not on the obligation, so reprocessing the
 * document cannot wipe it.
 */

const CADENCES = [
  { v: 'monthly',    label: 'Monthly' },
  { v: 'quarterly',  label: 'Quarterly' },
  { v: 'annual',     label: 'Annually' },
  { v: 'weekly',     label: 'Weekly' },
  { v: 'daily',      label: 'Daily' },
  { v: 'ad_hoc',     label: 'Ad hoc' },
  { v: 'continuous', label: 'Continuous' },
];

const ANCHORS = [
  { v: 'first', label: '1st' },
  { v: 'mid',   label: '15th' },
  { v: 'last',  label: 'Last day' },
  { v: 'day',   label: 'Specific…' },
];

const MONTHLY_LIKE = ['monthly', 'quarterly', 'annual'];

const TIMING = [
  { v: 'date',    label: 'On a date' },
  { v: 'trigger', label: 'On a trigger' },
];

// Units the contracts actually use. 'business_days' is recorded so the
// description matches the clause — business-day dates are NOT computed
// anywhere, since that needs a per-state holiday calendar.
const OFFSET_UNITS = [
  { v: 'business_days', label: 'business days' },
  { v: 'calendar_days', label: 'days' },
  { v: 'months',        label: 'months' },
];

const ordinal = (n) => {
  const s = (11 <= n % 100 && n % 100 <= 13) ? 'th'
    : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th');
  return `${n}${s}`;
};

const TaskRecurrence = ({ contractId, task, onChanged }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const timing = task.timing_type === 'trigger' ? 'trigger' : 'date';
  const isTrigger = timing === 'trigger';
  const [draftCondition, setDraftCondition] = useState(task.trigger_condition || '');
  const [draftOffset, setDraftOffset] = useState(
    task.trigger_offset_days == null ? '' : String(task.trigger_offset_days));
  React.useEffect(() => {
    setDraftCondition(task.trigger_condition || '');
    setDraftOffset(task.trigger_offset_days == null ? '' : String(task.trigger_offset_days));
  }, [task.trigger_condition, task.trigger_offset_days]);

  const cadence = (task.recurrence_pattern || '').toLowerCase();
  const anchorKind = task.anchor_kind || null;
  const anchorDay = task.anchor_day || 1;
  const isManual = Boolean(task.recurrence_is_manual);
  const showAnchor = MONTHLY_LIKE.includes(cadence);

  const save = async (patch) => {
    setSaving(true);
    setError('');
    try {
      const url = `${API_BASE_URL}/api/contracts/${contractId}/tasks/${encodeURIComponent(task.id)}/schedule`;
      // Always send the effective cadence alongside the anchor: the server
      // stores one row, so omitting cadence would clear a frequency the user
      // had previously chosen.
      const r = await axios.patch(url, { cadence, ...patch });
      onChanged?.(task.id, r.data);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (saving) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: 32, pl: 1 }}>
        <CircularProgress size={14} />
      </Box>
    );
  }

  return (
    <Tooltip title={error || (isManual ? '' : 'Suggested during document processing')}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {!isManual && (
            <AutoAwesomeIcon sx={{ fontSize: 12, color: 'primary.main', flexShrink: 0 }} />
          )}

          {/* Is this driven by the calendar, or by something happening? A
              trigger-based task has no date of its own, so the frequency and
              anchor controls are hidden rather than shown doing nothing. */}
          <Select
            value={timing}
            onChange={(e) => save(e.target.value === 'trigger'
              ? { timing_type: 'trigger',
                  trigger_condition: draftCondition || task.trigger_condition || '' }
              : { timing_type: 'date', cadence: cadence || 'monthly' })}
            size="small"
            sx={{ width: 118, height: 30, fontSize: '0.74rem' }}
          >
            {TIMING.map((t) => (
              <MenuItem key={t.v} value={t.v} sx={{ fontSize: '0.76rem' }}>{t.label}</MenuItem>
            ))}
          </Select>

          {!isTrigger && (
          <Select
            value={CADENCES.some((c) => c.v === cadence) ? cadence : ''}
            onChange={(e) => save({ cadence: e.target.value })}
            size="small"
            displayEmpty
            sx={{
              width: 104, height: 30, fontSize: '0.74rem',
              ...(error ? { '& fieldset': { borderColor: 'error.main' } } : {}),
            }}
            renderValue={(v) => (v
              ? CADENCES.find((c) => c.v === v)?.label
              : <Box component="span" sx={{ color: 'text.disabled' }}>Frequency</Box>)}
          >
            {CADENCES.map((c) => (
              <MenuItem key={c.v} value={c.v} sx={{ fontSize: '0.76rem' }}>{c.label}</MenuItem>
            ))}
          </Select>
          )}

          {!isTrigger && showAnchor && (
            <Select
              value={anchorKind || ''}
              onChange={(e) => save({
                anchor_kind: e.target.value,
                // 'Specific…' needs a number immediately or the server rejects
                // it; seed from the current value so the row stays valid.
                ...(e.target.value === 'day' ? { anchor_day: anchorDay } : {}),
              })}
              size="small"
              displayEmpty
              sx={{ width: 92, height: 30, fontSize: '0.74rem' }}
              renderValue={(v) => (v
                ? (v === 'day' ? ordinal(anchorDay) : ANCHORS.find((a) => a.v === v)?.label)
                : <Box component="span" sx={{ color: 'text.disabled' }}>Day…</Box>)}
            >
              {ANCHORS.map((a) => (
                <MenuItem key={a.v} value={a.v} sx={{ fontSize: '0.76rem' }}>{a.label}</MenuItem>
              ))}
            </Select>
          )}

          {!isTrigger && showAnchor && anchorKind === 'day' && (
            <Select
              value={anchorDay}
              onChange={(e) => save({ anchor_kind: 'day', anchor_day: Number(e.target.value) })}
              size="small"
              sx={{ width: 68, height: 30, fontSize: '0.74rem' }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => (
                <MenuItem key={n} value={n} sx={{ fontSize: '0.76rem' }}>{ordinal(n)}</MenuItem>
              ))}
            </Select>
          )}
        </Box>

        {isTrigger && (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
            {/* The condition is the whole point: without it, a trigger-based
                task can never be acted on. Committed on blur. */}
            <TextField
              value={draftCondition}
              onChange={(e) => setDraftCondition(e.target.value)}
              onBlur={() => {
                const v = draftCondition.trim();
                if (v && v !== (task.trigger_condition || '')) {
                  save({ timing_type: 'trigger', trigger_condition: v });
                }
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              size="small"
              placeholder="After what? e.g. AEMO issues its statement"
              sx={{ width: 236, '& .MuiOutlinedInput-root': { height: 30, fontSize: '0.72rem' } }}
            />
            <TextField
              value={draftOffset}
              onChange={(e) => setDraftOffset(e.target.value.replace(/[^0-9]/g, ''))}
              onBlur={() => {
                const n = draftOffset === '' ? null : Number(draftOffset);
                if (n !== (task.trigger_offset_days ?? null)) {
                  save({ timing_type: 'trigger',
                         trigger_condition: draftCondition.trim() || task.trigger_condition,
                         trigger_offset_days: n });
                }
              }}
              size="small"
              placeholder="N"
              sx={{ width: 54, '& .MuiOutlinedInput-root': { height: 30, fontSize: '0.72rem' } }}
            />
            <Select
              value={task.trigger_offset_unit || 'business_days'}
              onChange={(e) => save({
                timing_type: 'trigger',
                trigger_condition: draftCondition.trim() || task.trigger_condition,
                trigger_offset_unit: e.target.value,
              })}
              size="small"
              sx={{ width: 118, height: 30, fontSize: '0.72rem' }}
            >
              {OFFSET_UNITS.map((u) => (
                <MenuItem key={u.v} value={u.v} sx={{ fontSize: '0.74rem' }}>{u.label}</MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {/* Spell the rule out. "Monthly · last day" is unambiguous in a way that
            a date in this column never was. */}
        {task.recurrence_label && (
          <Typography sx={{ fontSize: '0.66rem', color: 'text.disabled', mt: 0.25 }}>
            {task.recurrence_label}
            {!isTrigger && showAnchor && !anchorKind && ' · day inferred from the extracted date'}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default TaskRecurrence;
